import React, { useState, useEffect } from "react";
import { NavLink, Link, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  LayoutDashboard,
  Car,
  Wrench,
  Tag,  // eslint-disable-line no-unused-vars
  Settings,
  Upload,
  LogOut,
  TrendingUp,
  Package,  // eslint-disable-line no-unused-vars
  ClipboardCheck,
  ListTodo,
  ChevronDown,
  Receipt,
  Banknote,  // eslint-disable-line no-unused-vars
  Menu,
  X,
  ExternalLink,
  FileSpreadsheet,
} from "lucide-react";

const navItems = [
  { to: "/admin/overblik", icon: LayoutDashboard, label: "Overblik", testid: "nav-dashboard", end: true },
  { to: "/admin/omsaetning", icon: TrendingUp, label: "Omsætning", testid: "nav-revenue" },
  { to: "/admin/omkostninger", icon: Receipt, label: "Omkostninger", testid: "nav-costs" },
  {
    key: "cars-group",
    icon: Car,
    label: "Biler",
    testid: "nav-cars-group",
    children: [
      { to: "/admin/alle-biler", label: "Alle biler", testid: "nav-all-cars" },
      { to: "/admin/biler", label: "GoMore biler", testid: "nav-cars" },
      { to: "/admin/carbuddi-biler", label: "Carbuddi biler", testid: "nav-carbuddi-cars" },
      { to: "/admin/lager", label: "Lagerbiler", testid: "nav-lager" },
      { to: "/admin/solgte", label: "Solgte biler", testid: "nav-sold" },
    ],
  },
  { to: "/admin/revisor", icon: Receipt, label: "Revisor", testid: "nav-accountant" },
  { to: "/admin/csv-historik", icon: FileSpreadsheet, label: "CSV historik", testid: "nav-csv-history" },
  { to: "/admin/opgaver", icon: ListTodo, label: "Mine opgaver", testid: "nav-tasks" },
  { to: "/admin/kontrol", icon: ClipboardCheck, label: "Kontrol & service", testid: "nav-kontrol" },
  {
    key: "web-group",
    icon: Upload,
    label: "Hjemmeside",
    testid: "nav-web-group",
    children: [
      { to: "/admin/hjemmeside", label: "Markedsføring", testid: "nav-marketing" },
      { to: "/admin/forespoergsler", label: "Forespørgsler", testid: "nav-bookings" },
    ],
  },
  { to: "/admin/indstillinger", icon: Settings, label: "Indstillinger", testid: "nav-settings" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [openGroups, setOpenGroups] = useState(() => ({
    "cars-group":
      location.pathname === "/admin/alle-biler" ||
      location.pathname === "/admin/biler" ||
      location.pathname === "/admin/carbuddi-biler" ||
      location.pathname === "/admin/lager" ||
      location.pathname === "/admin/solgte",
    "web-group":
      location.pathname === "/admin/hjemmeside" ||
      location.pathname === "/admin/forespoergsler",
  }));
  const [mobileOpen, setMobileOpen] = useState(false);

  const toggleGroup = (key) => setOpenGroups((s) => ({ ...s, [key]: !s[key] }));

  // Auto-luk mobil-menu når man navigerer
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const sidebar = (
    <>
      <div className="px-6 py-6 border-b border-zinc-800 flex items-center justify-between">
        <Link
          to="/admin/overblik"
          className="block group"
          data-testid="brand-home-sidebar"
          onClick={() => setMobileOpen(false)}
        >
          <div className="overline text-zinc-500 group-hover:text-zinc-300 transition-colors">Boutique flådestyring</div>
          <div className="font-display font-black text-2xl tracking-tighter mt-1 text-white group-hover:text-[#C8A661] transition-colors">MAHASA.</div>
        </Link>
        <button
          type="button"
          onClick={() => setMobileOpen(false)}
          className="lg:hidden text-zinc-400 hover:text-white p-1"
          data-testid="close-mobile-menu"
          aria-label="Luk menu"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
      <nav className="flex-1 py-4 overflow-y-auto">
        {navItems.map((it) => {
          if (it.children) {
            const isGroupActive = it.children.some((c) => location.pathname === c.to);
            const expanded = !!openGroups[it.key] || isGroupActive;
            return (
              <div key={it.key}>
                <button
                  type="button"
                  onClick={() => toggleGroup(it.key)}
                  className={"sidebar-link w-full " + (isGroupActive ? "active" : "")}
                  data-testid={it.testid}
                >
                  <it.icon className="w-4 h-4" strokeWidth={2} />
                  <span className="flex-1 text-left">{it.label}</span>
                  <ChevronDown
                    className={"w-3.5 h-3.5 transition-transform " + (expanded ? "rotate-180" : "")}
                    strokeWidth={2}
                  />
                </button>
                {expanded && (
                  <div className="pb-2">
                    {it.children.map((c) => (
                      <NavLink
                        key={c.to}
                        to={c.to}
                        data-testid={c.testid}
                        className={({ isActive }) =>
                          "flex items-center pl-11 pr-4 py-2 text-sm transition-colors " +
                          (isActive
                            ? "text-[#C8A661] bg-[#262626] border-l-2 border-[#C8A661]"
                            : "text-zinc-500 hover:text-white hover:bg-[#2A2A2A]")
                        }
                      >
                        {c.label}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          }
          return (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              data-testid={it.testid}
              className={({ isActive }) =>
                "sidebar-link " + (isActive ? "active" : "")
              }
            >
              <it.icon className="w-4 h-4" strokeWidth={2} />
              {it.label}
            </NavLink>
          );
        })}
      </nav>
      <div className="p-4 border-t border-zinc-800">
        <div className="text-xs text-zinc-500 mb-2 truncate">{user?.email}</div>
        <button
          onClick={() => {
            logout();
            navigate("/login");
          }}
          className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors"
          data-testid="logout-button"
        >
          <LogOut className="w-4 h-4" /> Log ud
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex" data-testid="app-layout">
      {/* Mobile topbar */}
      <header className="lg:hidden fixed top-0 inset-x-0 h-14 bg-[#1A1A1A] border-b border-zinc-800 z-40 flex items-center justify-between px-4">
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          className="text-zinc-200 p-1.5 -ml-1.5"
          data-testid="open-mobile-menu"
          aria-label="Åbn menu"
        >
          <Menu className="w-6 h-6" />
        </button>
        <Link
          to="/admin/overblik"
          className="font-display font-black text-base tracking-tighter text-white hover:text-[#C8A661] transition-colors"
          data-testid="brand-home-mobile"
        >
          MAHASA.
        </Link>
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-[#C8A661] transition-colors px-2 py-1 rounded-md border border-zinc-700"
          data-testid="back-to-public-mobile"
          aria-label="Til hjemmesiden"
        >
          <ExternalLink className="w-3 h-3" /> Site
        </Link>
      </header>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div
            className="absolute inset-0 bg-black/60"
            onClick={() => setMobileOpen(false)}
            data-testid="mobile-menu-backdrop"
          />
          <aside className="relative w-72 max-w-[85vw] bg-[#1A1A1A] border-r border-zinc-800 flex flex-col">
            {sidebar}
          </aside>
        </div>
      )}

      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-60 shrink-0 bg-[#1A1A1A] border-r border-zinc-800 flex-col">
        {sidebar}
      </aside>

      <main className="flex-1 min-w-0 bg-[#1A1A1A] pt-14 lg:pt-0 relative">
        {/* Desktop: floating "back to public site" link */}
        <Link
          to="/"
          className="hidden lg:inline-flex items-center gap-1.5 fixed top-5 right-6 z-30 px-3 py-1.5 rounded-full border border-zinc-700 bg-[#1A1A1A]/80 backdrop-blur-sm text-zinc-400 hover:text-[#C8A661] hover:border-[#C8A661]/60 text-xs uppercase tracking-[0.18em] transition-colors"
          data-testid="back-to-public-desktop"
        >
          <ExternalLink className="w-3 h-3" strokeWidth={2} /> Til hjemmesiden
        </Link>
        <Outlet />
      </main>
    </div>
  );
}
